import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { isObjectEmpty } from '../../../../utils/object-utils';
import {
  Container, DropdownContainer, Label, Option, OptionsContainer, Selected, Optional, NicknameInput, ClearFieldButton,
} from './css';
import SvgIcon from '../../icons/SvgIcon';

const Dropdown = ({
  input,
  placeholder,
  options,
  label,
  valueField = undefined,
  valueFormatter,
  preOption = {},
  optional,
  disabled,
  nicknameClick,
  nicknameInput,
  specialLabel,
  formData,
  page,
}) => {
  const [displayMode, setDisplayMode] = useState('none');
  const [nicknameValue, setNicknameValue] = useState('');
  const [nicknameSelected, setNicknameSelected] = useState();
  const containerRef = useRef(null);

  const getInputValuePlaceholder = () => {
    if (page === t('CreateShipment')) {
      if (formData && formData.readyTime.hours === '00' && formData.readyTime.minutes === '00') {
        return '';
      }

      return input.value;
    }

    if (input && input.value && ((!isObjectEmpty(preOption) && !preOption.isOptionActive) || isObjectEmpty(preOption))) {
      if (valueField) {
        const inputObject = options.filter((option) => option[valueField] === input.value || option[valueField] === input.value[valueField])[0];
        return valueFormatter ? valueFormatter(inputObject) : (specialLabel) ? t(inputObject) : inputObject;
      }

      return (specialLabel) ? t(input.value) : input.value;
    }

    if (!isObjectEmpty(preOption) && preOption.placeholder && preOption.isOptionActive) {
      return preOption.placeholder;
    }

    return placeholder;
  };

  const onPreOptionClick = () => {
    setNicknameSelected();
    input.onChange(null);
    preOption.handleOptionClick();
    setDisplayMode('none');
  };

  const onOptionClick = (e, option) => {
    let selected;

    if (valueField) {
      selected = (option && option[valueField]) ? option[valueField] : null;
    } else {
      selected = (option && option.value) ? option.value : null;
    }

    setNicknameSelected();
    input.onChange(selected);
    if (!isObjectEmpty(preOption) && preOption.isOptionActive && preOption.handleOtherOptionsClick) {
      preOption.handleOtherOptionsClick();
    }

    setDisplayMode('none');
  };

  const onNicknameCreateClick = () => {
    setNicknameSelected(nicknameValue);
    nicknameClick(nicknameValue);
    setDisplayMode('none');
  };

  const clearField = (e) => {
    e.preventDefault();
    onOptionClick(e, null);
    nicknameClick(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setDisplayMode('none');
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [displayMode]);

  const hasNicknameValue = (input && input.value && input.value.name);

  useEffect(() => {
    if (nicknameInput && !hasNicknameValue) {
      setDisplayMode('none');
    }
  }, [input.value]);

  return (
    <Container>
      <Label>
        {label}
        {' '}
        {optional && (
        <Optional>
          (
          {t('Optional')}
          )
        </Optional>
        )}
      </Label>
      <DropdownContainer
        ref={containerRef}
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
        }}
      >
        <Selected
          onClick={() => setDisplayMode(displayMode === 'block' ? 'none' : 'block')}
          hasPlaceholder={!((input.value || (!isObjectEmpty(preOption) && preOption.isOptionActive)))}
        >
          {nicknameSelected || getInputValuePlaceholder()}
          {(nicknameInput && hasNicknameValue)
            ? (
              <ClearFieldButton onClick={(e) => clearField(e)} role="button" ariaLabel={t('Clear')} tabIndex={0}>
                <SvgIcon icon="CloseIcon" />
              </ClearFieldButton>
            )
            : null}
        </Selected>
        <OptionsContainer displayMode={displayMode}>
          {nicknameInput && (
          <>
            <NicknameInput onChange={(e) => setNicknameValue(e.target.value)} />
            {nicknameValue !== ''
                        && (
                        <Option onMouseDown={(e) => onNicknameCreateClick(e)}>
                          {`${t('CreateNewContact')} '${nicknameValue}'`}
                        </Option>
                        )}
          </>
          )}

          {!isObjectEmpty(preOption) && preOption.handleOptionClick && preOption.placeholder && (
          <Option onClick={() => onPreOptionClick()}>
            {preOption.placeholder}
          </Option>
          )}
          {(nicknameInput ? options.filter((obj) => {
            const searchText = String(nicknameValue || '').trim().toLowerCase();

            if (!searchText) return true;

            return [obj.nickname, obj.value].filter(Boolean).some((field) =>
              String(field).toLowerCase().includes(searchText));
          }) : options).map((option, i) => (
            <Option
              key={`${option.value}-index-${i}`}
              onClick={(e) => onOptionClick(e, option)}
              value={option.value}
            >
              {(specialLabel) ? t(option.nickname || option.value) : (option.nickname || option.value)}
            </Option>
          ),
          )}
        </OptionsContainer>
      </DropdownContainer>
    </Container>
  );
};

Dropdown.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        name: PropTypes.string,
      }),
    ]),
    name: PropTypes.string,
    onChange: PropTypes.func,
  }),
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
    }),
  ),
  label: PropTypes.string,
  valueField: PropTypes.string,
  valueFormatter: PropTypes.func,
  preOption: PropTypes.shape({
    isOptionActive: PropTypes.bool,
    placeholder: PropTypes.string,
    handleOptionClick: PropTypes.func,
    handleOtherOptionsClick: PropTypes.func,
  }),
  optional: PropTypes.bool,
  disabled: PropTypes.bool,
  nicknameClick: PropTypes.func,
  nicknameInput: PropTypes.bool,
  specialLabel: PropTypes.bool,
  page: PropTypes.string,
  formData: PropTypes.shape({
    hasDroppingFreightOff: PropTypes.bool,
    droppingFreightOff: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.bool,
    }),
    regularScheduledPickup: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.bool,
    }),
    pickupDate: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.string,
    ]),
    readyTime: PropTypes.shape({
      hours: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      minutes: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      amOrPm: PropTypes.string,
    }),
    closingTime: PropTypes.shape({
      hours: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      minutes: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      amOrPm: PropTypes.string,
    }),
    attachedDocuments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    specialInstructions: PropTypes.string,
  }),
};

export default Dropdown;
